import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AnalyticsService } from './analytics.service';
import { Router } from '@angular/router';
import { isChunkLoadError } from '../utils/chunk-load-error';
import { UpdateNoticeService } from './update-notice.service';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private updatePromptShown = false; // Prevent multiple prompts

  constructor(private injector: Injector) {}

  handleError(error: Error | any): void {
    // Get AnalyticsService through injector to avoid circular dependency issues
    const analyticsService = this.injector.get(AnalyticsService);
    const router = this.injector.get(Router);

    // Extract error details
    const errorMessage = error?.message || error?.toString() || 'Unknown error';
    const errorStack = error?.stack || '';
    const currentUrl = router.url;

    // Filter out expected authentication flow errors - these are not real errors
    const expectedAuthErrors = [
      'login_required',           // OIDC: User needs to log in (expected)
      'interaction_required',     // OIDC: User interaction needed (expected)
      'consent_required',         // OIDC: User consent needed (expected)
      'account_selection_required' // OIDC: Account selection needed (expected)
    ];

    const isExpectedAuthError = expectedAuthErrors.some(expected =>
      errorMessage.toLowerCase().includes(expected.toLowerCase())
    );

    // Don't log expected authentication errors to analytics
    if (isExpectedAuthError) {
      console.log('Expected auth flow event (not an error):', errorMessage);
      return;
    }

    // Detect chunk loading failures (app version mismatch after deployment).
    // Covers the Chromium, Firefox and WebKit wordings — WebKit's "Importing a module
    // script failed." is what every iOS browser reports.
    if (isChunkLoadError(error)) {
      // First occurrence: silently reload once — a full page load converges the
      // tab onto the newest deployed version regardless of service-worker state
      // (version-skew rescue, see sw-update history 2026-08-04).
      if (this.tryAutoReloadOnce()) {
        return;
      }
      // Reload already attempted recently and chunks still fail (real outage or
      // persistent skew) — fall back to the explicit prompt instead of looping.
      if (!this.updatePromptShown) {
        this.updatePromptShown = true;
        this.handleAppUpdateRequired();
      }
      // Don't log chunk errors to analytics - they're deployment-related, not code errors
      return;
    }

    // Determine module and component from URL
    const urlSegments = currentUrl.split('/').filter(s => s);
    const module = urlSegments[0] || 'unknown';
    const component = urlSegments[urlSegments.length - 1] || 'unknown';

    // Extract HTTP error details if this is an HttpErrorResponse
    const httpErrorDetails = this.extractHttpErrorDetails(error);

    // Log error to analytics
    analyticsService.trackError(
      error,
      module,
      component,
      {
        // Keep explicit fields for backwards compatibility/searching
        message: errorMessage,
        stack: errorStack,
        url: currentUrl,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        errorType: error?.constructor?.name || 'Error',
        ...httpErrorDetails
      }
    );

    // Also log to console for development
    console.error('Global Error Handler:', error);

    // Re-throw in development mode for debugging
    if (!error?.skipRethrow) {
      // Don't block the application, but log it
      console.error('Error details:', {
        message: errorMessage,
        stack: errorStack,
        url: currentUrl
      });
    }
  }

  /**
   * Extract HTTP error details from HttpErrorResponse
   */
  private extractHttpErrorDetails(error: any): Record<string, any> {
    if (!(error instanceof HttpErrorResponse)) {
      return {};
    }

    const details: Record<string, any> = {
      httpStatus: error.status,
      httpStatusText: error.statusText
    };

    // Extract server error message from various possible formats
    if (error.error) {
      if (typeof error.error === 'string') {
        details.serverMessage = error.error;
      } else if (error.error instanceof ErrorEvent) {
        details.serverMessage = error.error.message;
      } else if (typeof error.error === 'object') {
        // Server might return structured error: { message: '...', details: '...', errors: [...] }
        details.serverMessage = error.error.message || error.error.error || error.error.title || JSON.stringify(error.error);
        if (error.error.details) details.serverDetails = error.error.details;
        if (error.error.errors) details.validationErrors = error.error.errors;
      }
    }

    // Include request URL and method if available
    if (error.url) details.requestUrl = error.url;
    if (error.headers) {
      const method = error.headers.get('X-Http-Method');
      if (method) details.httpMethod = method;
    }

    return details;
  }

  /**
   * Reload the page once per minute at most when a lazy chunk fails to load.
   * sessionStorage-guarded so a reload that doesn't cure the failure (server
   * outage, genuinely missing asset) cannot cause an infinite reload loop —
   * the second failure within the window falls through to the visible prompt.
   */
  private tryAutoReloadOnce(): boolean {
    const AUTO_RELOAD_KEY = 'chunkErrorAutoReloadAt';
    try {
      const last = Number(sessionStorage.getItem(AUTO_RELOAD_KEY) || 0);
      if (Date.now() - last < 60_000) {
        return false;
      }
      sessionStorage.setItem(AUTO_RELOAD_KEY, String(Date.now()));
      console.warn('Chunk load failed — auto-reloading once to pick up the current app version.');
      this.reloadPage();
      return true;
    } catch {
      return false; // sessionStorage unavailable — use the prompt path
    }
  }

  /**
   * Handle app update required scenario
   * Shows the calm update notice (ri-update-banner) and reloads on the user's "Päivitä nyt"
   */
  private handleAppUpdateRequired(): void {
    console.warn('App update detected - chunk loading failed. New version available.');

    // One clear, persistent notice (no second native confirm()). The user's own press of
    // "Päivitä nyt" reloads; the once-a-minute auto-reload above stays the only automatic one.
    // afterDismissed (Myöhemmin, close, or another snackbar replacing it) re-arms the prompt so
    // a later chunk failure shows the banner again instead of failing silently.
    this.injector.get(UpdateNoticeService).show(
      'chunk-failed',
      () => this.reloadPage(),
      () => {
        this.updatePromptShown = false;
      }
    );
  }

  /** Overridable seam for tests. */
  protected reloadPage(): void {
    window.location.reload();
  }
}
